import { FormControlLabel } from '@mui/material';
import { useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useConfig } from '@revenuewell/logic-config';
import { PrimaryButton, SecondaryButton } from 'apps/wrapper/src/components/shared/buttons';
import { OrangeGreenSwitch } from 'apps/wrapper/src/components/shared/switch/switch.styles';
import { useEventBus } from 'apps/wrapper/src/hooks/use-event-bus';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentRequestStatus } from 'apps/wrapper/src/services/appointment/types/appointment-request-status';
import moment from 'moment-timezone';
import { FC, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useApptRequestsWidget } from '../../hooks/use-appt-requests-widget';
import { useCancellationRequestView } from '../../hooks/use-cancellation-request-view';
import { AiTranscriptSection } from '../../shared/ai-transcript/ai-transcript-section';
import { AppointmentDetailsSection } from '../../shared/appointment-details/appointment-details-section';
import { InsuranceInfoSection } from '../../shared/insurance-info-section';
import { NotesSection } from '../../shared/notes-section/notes-section';
import { PmsStatusSection } from '../../shared/pms-status/pms-status-section';
import { Page, PageContent, PageTitle, Toolbar } from '../styles';
import { CancellationRequestViewSkeleton } from './cancellation-request.skeleton';
import { PatientDetailsSection } from './sections/patient-details/patient-details-section';

export const CancellationRequestView: FC = () => {
  const { config } = useConfig();
  const { oidcService, getClaims } = useOidcWithAdmin();
  const { showError, showSuccess } = useNotification();
  const { publishToIframes, publish } = useEventBus();
  const { locationId, goBack, markRequestAsRead } = useApptRequestsWidget();
  const { isLoading, requestDetails, appointment, isEditable } = useCancellationRequestView();
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [notifyPatient, setNotifyPatient] = useState(false);
  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  useEffect(() => {
    if (!requestDetails) return;

    publishToIframes({ messageType: 'appt-request-reset' });
    markRequestAsRead(requestDetails.id);
  }, [requestDetails]);

  const handleRemoveClick = async () => {
    if (!appointment) return;

    try {
      setIsCancelling(true);
      await appointmentService.acceptCancelAppointment(requestDetails.id, locationId);
      publishToIframes({ messageType: 'appt-request-reset' });
      publishToIframes({ messageType: 'appt-request-refresh' });
      showSuccess('Appointment cancelled');

      if (notifyPatient && requestDetails.patient?.phone && appointment) {
        const message = `Hello ${requestDetails.patient?.firstName}, your appointment on ${moment(
          appointment.dateStart
        ).format('MMMM Do YYYY, h:mm a')} has been canceled. Please contact us if you’d like to reschedule.`;

        publish({
          messageType: 'inboxCommunications',
          data: {
            patientId: requestDetails.patient?.id,
            phoneNumber: requestDetails.patient?.phone,
            locationId,
            message
          }
        });
      }

      goBack(true);
    } catch {
      showError('Unable to cancel appointment.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDismissRequestClick = async () => {
    try {
      setIsDismissing(true);
      const claims = await getClaims();

      await appointmentService.dismissAppointmentRequest(requestDetails.id, locationId, claims.masterAccountId);
      publishToIframes({ messageType: 'appt-request-reset' });
      publishToIframes({ messageType: 'appt-request-refresh' });
      showSuccess('Appointment request dismissed');

      if (notifyPatient && requestDetails.patient?.phone && appointment) {
        const apptTime = moment(appointment.dateStart).format('MMMM Do YYYY, h:mm a');
        const message = `Hello ${requestDetails.patient?.firstName}, unfortunately, we’re unable to process the cancellation request for your appointment scheduled on ${apptTime}.`;

        publish({
          messageType: 'inboxCommunications',
          data: {
            patientId: requestDetails.patient?.id,
            phoneNumber: requestDetails.patient?.phone,
            locationId,
            message
          }
        });
      }

      goBack(true);
    } catch {
      showError('Unable to dismiss appointment request');
    } finally {
      setIsDismissing(false);
    }
  };

  if (isLoading) return <CancellationRequestViewSkeleton />;

  return (
    <Page>
      <PageTitle>Cancellation Request</PageTitle>
      <PageContent sx={{ margin: '0' }}>
        <Scrollbars autoHide>
          <PatientDetailsSection />
          <InsuranceInfoSection insuranceInfo={requestDetails.insuranceInfo} />
          {appointment && (
            <AppointmentDetailsSection
              title="Date & Time"
              isLoading={isLoading}
              appointmentId={appointment.appointmentId}
              startTime={appointment.dateStart}
              operatoryName={appointment.operatoryName}
              providerName={appointment.providerName}
              hideControls={!isEditable}
            />
          )}
          <AiTranscriptSection
            isLoading={isLoading}
            caseId={requestDetails.milaCaseId}
            patientId={requestDetails.patient?.id}
          />
          {(requestDetails.isDirectlyBooked ||
            requestDetails.status === AppointmentRequestStatus.Confirmed ||
            isLoading) && (
            <PmsStatusSection
              isLoading={isLoading}
              pmsWritebackStatus={requestDetails.pmsWritebackStatus}
              appointmentId={appointment?.appointmentId}
            />
          )}
          <NotesSection locationId={locationId} requestDetails={requestDetails} />
        </Scrollbars>
        <Toolbar sx={{ flexDirection: 'column', borderTop: 'none' }}>
          {(requestDetails.status == AppointmentRequestStatus.New ||
            requestDetails.status == AppointmentRequestStatus.Pending) &&
            requestDetails.patient?.phone && (
              <FormControlLabel
                label="Notify patient"
                control={
                  <OrangeGreenSwitch
                    color="default"
                    checked={notifyPatient}
                    onChange={() => setNotifyPatient(!notifyPatient)}
                  />
                }
              />
            )}
          <PrimaryButton
            loading={isCancelling}
            disabled={isLoading || isDismissing || !isEditable}
            onClick={handleRemoveClick}
            sx={{ marginBottom: '15px' }}
            disableElevation
          >
            Remove from Schedule
          </PrimaryButton>
          <SecondaryButton
            loading={isDismissing}
            disabled={isLoading || isCancelling || !isEditable}
            onClick={handleDismissRequestClick}
            disableElevation
          >
            Dismiss
          </SecondaryButton>
        </Toolbar>
      </PageContent>
    </Page>
  );
};
